import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from './ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { formatCurrency, formatDate } from '../lib/utils';
import { expenseAPI } from '../lib/api';
import { toast } from 'sonner';
import {
  Fuel,
  UtensilsCrossed,
  Wrench,
  PenLine,
  Film,
  Car,
  Zap,
  Folder,
  Receipt,
  ShoppingBag,
  X,
  Pencil,
  Loader2,
  Paperclip,
} from 'lucide-react';

const iconMap = {
  fuel: Fuel,
  utensils: UtensilsCrossed,
  wrench: Wrench,
  pen: PenLine,
  film: Film,
  car: Car,
  zap: Zap,
  folder: Folder,
  receipt: Receipt,
  shopping: ShoppingBag,
};

const CategoryIcon = ({ icon, className }) => {
  const IconComponent = iconMap[icon] || Receipt;
  return <IconComponent className={className} />;
};

export const ExpenseList = ({
  expenses,
  categories,
  selectedCategory,
  onCategoryChange,
  onDelete,
  onUpdate,
}) => {
  const [editingExpense, setEditingExpense] = useState(null);
  const [editForm, setEditForm] = useState({
    amount: '',
    description: '',
    category_id: '',
    date: '',
    tax_amount: '',
    tip_amount: '',
  });
  const [saving, setSaving] = useState(false);
  const [editReceiptImage, setEditReceiptImage] = useState(null);
  const [loadingEditReceipt, setLoadingEditReceipt] = useState(false);
  const [receiptModal, setReceiptModal] = useState({ open: false, loading: false, image: null, expense: null });

  // Load receipt when editing an expense that has one
  useEffect(() => {
    if (editingExpense?.has_receipt) {
      setLoadingEditReceipt(true);
      expenseAPI.getReceipt(editingExpense.id)
        .then(response => {
          setEditReceiptImage(response.data.receipt_image);
        })
        .catch(() => {
          setEditReceiptImage(null);
        })
        .finally(() => {
          setLoadingEditReceipt(false);
        });
    } else {
      setEditReceiptImage(null);
    }
  }, [editingExpense]);

  const handleEditClick = (expense) => {
    setEditingExpense(expense);
    setEditForm({
      amount: expense.amount.toString(),
      description: expense.description,
      category_id: expense.category_id,
      date: expense.date,
      tax_amount: (expense.tax_amount || 0).toString(),
      tip_amount: (expense.tip_amount || 0).toString(),
    });
  };

  const handleSave = async () => {
    if (!editingExpense) return;
    
    setSaving(true);
    try {
      await onUpdate(editingExpense.id, {
        amount: parseFloat(editForm.amount),
        description: editForm.description,
        category_id: editForm.category_id,
        date: editForm.date,
        tax_amount: parseFloat(editForm.tax_amount) || 0,
        tip_amount: parseFloat(editForm.tip_amount) || 0,
      });
      setEditingExpense(null);
      setEditReceiptImage(null);
    } finally {
      setSaving(false);
    }
  };

  const handleCloseEdit = () => {
    setEditingExpense(null);
    setEditReceiptImage(null);
  };

  const handleViewReceipt = async (expense) => {
    setReceiptModal({ open: true, loading: true, image: null, expense });
    try {
      const response = await expenseAPI.getReceipt(expense.id);
      setReceiptModal({ open: true, loading: false, image: response.data.receipt_image, expense });
    } catch (error) {
      toast.error('Failed to load receipt');
      setReceiptModal({ open: false, loading: false, image: null, expense: null });
    }
  };

  // Calculate subtotal from form values
  const getSubtotal = () => {
    const total = parseFloat(editForm.amount) || 0;
    const tax = parseFloat(editForm.tax_amount) || 0;
    const tip = parseFloat(editForm.tip_amount) || 0;
    return (total - tax - tip).toFixed(2);
  };

  return (
    <>
      <Card className="bg-card border-white/10" data-testid="expense-list-card">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-medium text-muted-foreground tracking-wide uppercase">
            Transactions
          </CardTitle>
          <Select value={selectedCategory || 'all'} onValueChange={onCategoryChange}>
            <SelectTrigger
              className="w-[140px] bg-secondary/50 border-white/10 text-sm"
              data-testid="category-filter-select"
            >
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent className="bg-card border-white/10">
              <SelectItem value="all">All Categories</SelectItem>
              {categories?.map((cat) => (
                <SelectItem key={cat.id} value={cat.id}>
                  {cat.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent className="space-y-2">
          {expenses?.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground" data-testid="no-expenses-message">
              No expenses found
            </div>
          ) : (
            expenses?.map((expense) => {
              const taxAmount = expense.tax_amount || 0;
              const tipAmount = expense.tip_amount || 0;
              const subtotal = expense.amount - taxAmount - tipAmount;
              const hasConversion = expense.original_amount && expense.original_currency;
              
              return (
                <div
                  key={expense.id}
                  className="expense-card flex items-center justify-between p-3 rounded-xl bg-secondary/30 border border-white/5"
                  data-testid={`expense-item-${expense.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="category-icon">
                      <CategoryIcon
                        icon={expense.category_icon}
                        className="w-5 h-5 text-muted-foreground"
                      />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm">{expense.description}</p>
                        {expense.has_receipt && (
                          <button
                            onClick={() => handleViewReceipt(expense)}
                            className="text-primary hover:text-primary/80 transition-colors"
                            title="View receipt"
                            data-testid={`view-receipt-${expense.id}`}
                          >
                            <Paperclip className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(expense.date)} • {expense.category_name}
                      </p>
                      {hasConversion && (
                        <p className="text-xs text-amber-400">
                          {expense.original_currency} ${expense.original_amount.toFixed(2)} → CAD @ {expense.exchange_rate.toFixed(4)}
                        </p>
                      )}
                      {(taxAmount > 0 || tipAmount > 0) && (
                        <p className="text-xs text-muted-foreground">
                          Subtotal: {formatCurrency(subtotal)}
                          {taxAmount > 0 && <span className="text-primary ml-1">+ Tax: {formatCurrency(taxAmount)}</span>}
                          {tipAmount > 0 && <span className="text-green-400 ml-1">+ Tip: {formatCurrency(tipAmount)}</span>}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold tabular-nums text-red-400 mr-2">
                      -{formatCurrency(expense.amount)}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEditClick(expense)}
                      className="h-8 w-8 text-muted-foreground hover:text-primary hover:bg-primary/10"
                      data-testid={`edit-expense-${expense.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                          data-testid={`delete-expense-${expense.id}`}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-card border-white/10">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete expense?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will permanently delete the expense "{expense.description}".
                            This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="bg-secondary border-white/10">
                            Cancel
                          </AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => onDelete(expense.id)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            data-testid={`confirm-delete-${expense.id}`}
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              );
            })
          )}
        </CardContent>
      </Card>

      {/* Edit Expense Dialog */}
      <Dialog open={!!editingExpense} onOpenChange={(open) => !open && handleCloseEdit()}>
        <DialogContent className="bg-card border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Expense</DialogTitle>
            <DialogDescription>
              Update the expense details below
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid md:grid-cols-2 gap-6 py-4">
            {/* Form Fields */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-description">Description</Label>
                <Input
                  id="edit-description"
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  className="bg-input/50 border-white/10"
                  data-testid="edit-expense-description"
                />
              </div>
              
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="edit-amount">Total (CAD)</Label>
                  <Input
                    id="edit-amount"
                    type="number"
                    step="0.01"
                    value={editForm.amount}
                    onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
                    className="bg-input/50 border-white/10"
                    data-testid="edit-expense-amount"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-tax">Tax</Label>
                  <Input
                    id="edit-tax"
                    type="number"
                    step="0.01"
                    value={editForm.tax_amount}
                    onChange={(e) => setEditForm({ ...editForm, tax_amount: e.target.value })}
                    className="bg-input/50 border-white/10"
                    data-testid="edit-expense-tax"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-tip">Tip</Label>
                  <Input
                    id="edit-tip"
                    type="number"
                    step="0.01"
                    value={editForm.tip_amount}
                    onChange={(e) => setEditForm({ ...editForm, tip_amount: e.target.value })}
                    className="bg-input/50 border-white/10"
                    data-testid="edit-expense-tip"
                  />
                </div>
              </div>
              
              {/* Calculated Subtotal */}
              <div className="p-3 rounded-lg bg-primary/10 border border-primary/20">
                <p className="text-sm text-muted-foreground">Subtotal (before tax)</p>
                <p className="text-lg font-semibold text-primary">{formatCurrency(parseFloat(getSubtotal()))}</p>
              </div>
              
              <div className="space-y-2">
                <Label>Category</Label>
                <Select
                  value={editForm.category_id}
                  onValueChange={(value) => setEditForm({ ...editForm, category_id: value })}
                >
                  <SelectTrigger
                    className="bg-input/50 border-white/10"
                    data-testid="edit-expense-category"
                  >
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-white/10">
                    {categories?.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-date">Date</Label>
                <Input
                  id="edit-date"
                  type="date"
                  value={editForm.date}
                  onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                  className="bg-input/50 border-white/10"
                  data-testid="edit-expense-date"
                />
              </div>
            </div>
            
            {/* Receipt Image */}
            <div className="space-y-2">
              <Label>Receipt</Label>
              <div className="border border-white/10 rounded-lg overflow-hidden bg-secondary/20 min-h-[200px] flex items-center justify-center">
                {loadingEditReceipt ? (
                  <div className="flex flex-col items-center gap-2 py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                    <p className="text-sm text-muted-foreground">Loading receipt...</p>
                  </div>
                ) : editReceiptImage ? (
                  <img
                    src={`data:image/jpeg;base64,${editReceiptImage}`}
                    alt="Receipt"
                    className="w-full h-auto max-h-[400px] object-contain"
                  />
                ) : (
                  <div className="flex flex-col items-center gap-2 py-8 text-muted-foreground">
                    <Receipt className="w-8 h-8" />
                    <p className="text-sm">No receipt attached</p>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={handleCloseEdit}
              className="border-white/10"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || !editForm.amount || !editForm.description}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="save-expense-btn"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receipt Viewer Modal */}
      <Dialog open={receiptModal.open} onOpenChange={(open) => !open && setReceiptModal({ open: false, loading: false, image: null, expense: null })}>
        <DialogContent className="bg-card border-white/10 max-w-2xl">
          <DialogHeader>
            <DialogTitle>Receipt</DialogTitle>
            <DialogDescription>
              {receiptModal.expense?.description} - {formatCurrency(receiptModal.expense?.amount || 0)}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {receiptModal.loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : receiptModal.image ? (
              <img
                src={`data:image/jpeg;base64,${receiptModal.image}`}
                alt="Receipt"
                className="w-full rounded-lg border border-white/10"
              />
            ) : (
              <p className="text-center text-muted-foreground py-12">No receipt image available</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
